import { useEffect, useRef, useState } from "react";
import { brl, effectivePrice } from "@/lib/api";
import { AlertTriangle, Minus, Plus, ShoppingCart } from "lucide-react";
import { Modal } from "@/components/panel/ui";
import { Button, Input } from "@/components/ui-kit";

/**
 * Confirmação do item antes de ele entrar na venda.
 *
 * O clique numa variação da busca colocava o produto no carrinho na hora, com
 * quantidade 1 e sem conferência. Dois erros nasciam daí: a variação vizinha
 * (P e M ficam a poucos pixels uma da outra) e a quantidade, que só era
 * corrigida depois, no carrinho, com o cliente esperando.
 *
 * A leitura por código de barras NÃO passa por aqui, de propósito: o scanner é
 * uma confirmação em si — o vendedor apontou para aquele produto —, e um modal
 * a cada bipe dobraria o número de ações numa fila de balcão.
 *
 * Enter confirma. O campo de quantidade abre focado e selecionado, então digitar
 * "3" e apertar Enter é o caminho inteiro.
 */
export default function AddItemDialog({ product, variant, onClose, onConfirm }) {
  const [qty, setQty] = useState("1");
  const qtyRef = useRef(null);

  useEffect(() => { qtyRef.current?.select(); }, []);

  if (!product || !variant) return null;

  const preco = effectivePrice(product, variant);
  const quantidade = Math.max(1, parseInt(qty, 10) || 0);
  // Vender acima do saldo é PERMITIDO no balcão — o cliente está com a peça na
  // mão, e o saldo negativo é só a entrada que ainda não foi lançada. Então
  // isto avisa, não bloqueia: a trava anterior recusava a venda de mercadoria
  // que estava fisicamente ali.
  const saldoDepois = variant.stock - quantidade;
  const excede = saldoDepois < 0;

  const confirmar = () => onConfirm(product, variant, quantidade);

  return (
    <Modal open onClose={onClose} title="Adicionar à venda" testId="pos-add-item-dialog">
      <form onSubmit={(e) => { e.preventDefault(); confirmar(); }}>
        <div className="space-y-1">
          <p className="font-semibold text-slate-900 leading-snug">{product.name}</p>
          <p className="text-sm text-slate-500">
            {product.brand && <span className="font-medium text-slate-600">{product.brand} · </span>}
            {variant.label}
            <span className="font-mono text-slate-400"> · {variant.stock} em estoque</span>
          </p>
        </div>

        <div className="flex items-end justify-between gap-4 mt-5">
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-slate-500 mb-1" htmlFor="pos-add-qty">
              Quantidade
            </label>
            <div className="flex items-center gap-1.5">
              {/* 44px de alvo: é o mesmo dedo com pressa do resto do PDV. */}
              <Button type="button" variant="secondary" size="icon" aria-label="Diminuir quantidade"
                onClick={() => setQty(String(Math.max(1, quantidade - 1)))} data-testid="pos-add-qty-minus">
                <Minus size={16} />
              </Button>
              <Input ref={qtyRef} id="pos-add-qty" type="number" min="1" inputMode="numeric" mono
                value={qty} onChange={(e) => setQty(e.target.value)}
                aria-label="Quantidade a adicionar" aria-invalid={excede || undefined}
                className={`w-20 text-center ${excede ? "border-red-300" : ""}`}
                data-testid="pos-add-qty" autoFocus />
              <Button type="button" variant="secondary" size="icon" aria-label="Aumentar quantidade"
                onClick={() => setQty(String(quantidade + 1))} data-testid="pos-add-qty-plus">
                <Plus size={16} />
              </Button>
            </div>
          </div>

          <div className="text-right">
            <p className="text-xs text-slate-500">Unitário <span className="font-mono">{brl(preco)}</span></p>
            <p className="font-mono text-xl font-semibold text-slate-900" data-testid="pos-add-total">
              {brl(preco * quantidade)}
            </p>
          </div>
        </div>

        {/* Aviso, não impedimento: quem está no balcão vê a peça e sabe que ela
            existe. O que o sistema tem a dizer é que o cadastro vai ficar
            devendo — e quanto. */}
        {excede && (
          <div role="alert" className="flex gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 mt-4"
            data-testid="pos-add-stock-warning">
            <AlertTriangle size={16} className="text-amber-600 shrink-0 mt-0.5" aria-hidden />
            <p className="text-xs text-amber-800 leading-relaxed">
              O cadastro tem <b className="font-mono">{variant.stock}</b> em estoque.
              A venda pode sair mesmo assim — o saldo fica em{" "}
              <b className="font-mono">{saldoDepois}</b> e volta ao normal quando a entrada
              for lançada (XML ou ajuste manual).
            </p>
          </div>
        )}

        <div className="flex gap-3 mt-6">
          <Button type="button" variant="secondary" block onClick={onClose} data-testid="pos-add-cancel">
            Cancelar
          </Button>
          <Button type="submit" icon={ShoppingCart} block data-testid="pos-add-confirm">
            Adicionar
          </Button>
        </div>
      </form>
    </Modal>
  );
}
